<script setup>
import { reactive, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      gender: 'neutral',
      responseStyle: 'adaptive',
      aiModel: 'default',
      country: '',
      preferredLanguage: 'en',
    }),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const localValue = reactive({ ...props.modelValue });

// Gender options
const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'neutral', label: 'Neutral' },
];

// Response Style options
const responseStyleOptions = [
  { value: 'adaptive', label: 'Adaptive and Versatile' },
  { value: 'professional', label: 'Professional and Formal' },
  { value: 'friendly', label: 'Friendly and Casual' },
  { value: 'concise', label: 'Concise and Direct' },
  { value: 'detailed', label: 'Detailed and Thorough' },
  { value: 'empathetic', label: 'Empathetic and Supportive' },
];

// AI Model options
const aiModelOptions = [
  { value: 'default', label: 'Standard', credits: 1 },
  { value: 'balanced', label: 'Balanced', credits: 2 },
  { value: 'sharper', label: 'Sharper', credits: 4 },
  { value: 'advanced', label: 'Advanced', credits: 8 },
];

// Country options
const countryOptions = [
  { value: '', label: 'Select a Country' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'SG', label: 'Singapore' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'TH', label: 'Thailand' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'PH', label: 'Philippines' },
  { value: 'TW', label: 'Taiwan' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'CN', label: 'China' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
];

// Language options
const languageOptions = [
  { value: 'en', label: 'English' },
  { value: 'zh-CN', label: '简体中文' },
  { value: 'zh-TW', label: '繁體中文' },
  { value: 'ms', label: 'Bahasa Malaysia' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'th', label: 'ไทย' },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
];

// Get credits for selected model
const selectedModelCredits = computed(() => {
  const model = aiModelOptions.find(m => m.value === localValue.aiModel);
  return model ? model.credits : 1;
});

// Watch for external changes
watch(
  () => props.modelValue,
  newValue => {
    Object.assign(localValue, newValue);
  },
  { deep: true }
);

// Emit changes
watch(
  localValue,
  newValue => {
    emit('update:modelValue', { ...newValue });
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-5">
    <!-- Gender -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Gender<span class="text-ruby-9">*</span>
      </label>
      <select
        v-model="localValue.gender"
        class="w-full px-4 py-3 text-sm bg-white dark:bg-n-slate-2 border border-n-slate-4 rounded-xl text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all"
      >
        <option
          v-for="option in genderOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <!-- Response Style -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Response Style<span class="text-ruby-9">*</span>
      </label>
      <select
        v-model="localValue.responseStyle"
        class="w-full px-4 py-3 text-sm bg-white dark:bg-n-slate-2 border border-n-slate-4 rounded-xl text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all"
      >
        <option
          v-for="option in responseStyleOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <!-- AI Model -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        AI Model<span class="text-ruby-9">*</span>
      </label>
      <select
        v-model="localValue.aiModel"
        class="w-full px-4 py-3 text-sm bg-white dark:bg-n-slate-2 border border-n-slate-4 rounded-xl text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all"
      >
        <option
          v-for="option in aiModelOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <p class="text-xs text-n-slate-10">
        Credits charged per request: {{ selectedModelCredits }} Credit(s)
      </p>
    </div>

    <!-- Country -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Country<span class="text-ruby-9">*</span>
      </label>
      <select
        v-model="localValue.country"
        class="w-full px-4 py-3 text-sm bg-white dark:bg-n-slate-2 border border-n-slate-4 rounded-xl text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all"
      >
        <option
          v-for="option in countryOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <!-- Preferred Language -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Preferred Language<span class="text-ruby-9">*</span>
      </label>
      <select
        v-model="localValue.preferredLanguage"
        class="w-full px-4 py-3 text-sm bg-white dark:bg-n-slate-2 border border-n-slate-4 rounded-xl text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all"
      >
        <option
          v-for="option in languageOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>
  </div>
</template>
